import { http } from './client';
import type {
  ChatKind,
  ChatSessionDetail,
  ChatSessionMeta,
  ChatSessionStatus,
  ChatStoredMessage,
  ChatTurnResponse,
  Language,
  PageResult,
} from './types';

export interface ListSessionsParams {
  kind?: ChatKind;
  status?: ChatSessionStatus;
  limit?: number;
  cursor?: string;
}

export interface ListMessagesParams {
  limit?: number;
  cursor?: string;
  ascending?: boolean;
}

export const chatApi = {
  async createSession(
    input: { kind: ChatKind; title?: string; promptVersion?: number },
    lang?: Language,
  ): Promise<ChatSessionMeta> {
    const res = await http.post<ChatSessionMeta>('/v1/bot/chat/sessions', input, {
      headers: lang ? { 'Accept-Language': lang } : undefined,
    });
    return res.data;
  },

  async listOwnSessions(params: ListSessionsParams = {}): Promise<PageResult<ChatSessionMeta>> {
    const res = await http.get<PageResult<ChatSessionMeta>>('/v1/bot/chat/sessions', { params });
    return res.data;
  },

  async getSession(sessionId: string, params: ListMessagesParams = {}): Promise<ChatSessionDetail> {
    const res = await http.get<ChatSessionDetail>(
      `/v1/bot/chat/sessions/${encodeURIComponent(sessionId)}`,
      { params },
    );
    return res.data;
  },

  async closeSession(sessionId: string): Promise<{ status: 'closed'; sessionId: string }> {
    const res = await http.delete<{ status: 'closed'; sessionId: string }>(
      `/v1/bot/chat/sessions/${encodeURIComponent(sessionId)}`,
    );
    return res.data;
  },

  async deleteSession(sessionId: string): Promise<{ deleted: number; sessionId: string }> {
    const res = await http.delete<{ deleted: number; sessionId: string }>(
      `/v1/bot/chat/sessions/${encodeURIComponent(sessionId)}/purge`,
    );
    return res.data;
  },

  async postMessageSync(
    sessionId: string,
    input: { message: string; expectedLastMessageId?: string | null },
  ): Promise<ChatTurnResponse> {
    const res = await http.post<ChatTurnResponse>(
      `/v1/bot/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
      input,
    );
    return res.data;
  },

  async getAllMessages(sessionId: string): Promise<ChatStoredMessage[]> {
    const out: ChatStoredMessage[] = [];
    let cursor: string | undefined;
    for (let i = 0; i < 10; i++) {
      const page = await chatApi.getSession(sessionId, {
        limit: 200,
        ascending: true,
        ...(cursor ? { cursor } : {}),
      });
      out.push(...page.messages.items);
      cursor = page.messages.nextCursor;
      if (!cursor) break;
    }
    return out;
  },
};
